import { useMemo, useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export const Terrain3D = ({ open, onOpenChange, sourceImageData, colorImageData }) => {
  const [heightScale, setHeightScale] = useState(0.05);
  const [autoRotate, setAutoRotate] = useState(true);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="terrain-3d-dialog"
        className="max-w-4xl bg-[#0f1015] border-zinc-800"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-100">3D Terrain Peek</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Drag to orbit · scroll to zoom · right-click drag to pan.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-4 rounded-xl bg-[#0d0e13] border border-zinc-800 px-4 py-2">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold whitespace-nowrap">
              Height intensity
            </label>
            <input
              data-testid="3d-height-intensity"
              type="range"
              min={0}
              max={2}
              step={0.01}
              value={heightScale}
              onChange={(e) => setHeightScale(Number(e.target.value))}
              className="flex-1 accent-rose-500"
            />
            <span className="text-[10px] mono text-zinc-400 w-10 text-right">
              {heightScale.toFixed(2)}x
            </span>
          </div>
          <button
            data-testid="3d-autorotate-toggle"
            onClick={() => setAutoRotate((v) => !v)}
            className={`text-[11px] font-medium border rounded-full px-3 py-1.5 transition ${
              autoRotate
                ? "brand-gradient text-white border-transparent"
                : "text-zinc-300 hover:text-white bg-zinc-800/70 border-zinc-700/60"
            }`}
          >
            Auto-rotate {autoRotate ? "on" : "off"}
          </button>
        </div>

        <div
          data-testid="terrain-3d-viewport"
          className="rounded-xl bg-[#080910] border border-zinc-800 h-[520px] overflow-hidden"
        >
          {sourceImageData && colorImageData ? (
            <Canvas
              camera={{ position: [1.4, 1.1, 1.4], fov: 45 }}
              dpr={[1, 2]}
            >
              <color attach="background" args={["#080910"]} />
              <ambientLight intensity={0.55} />
              <directionalLight position={[3, 5, 2]} intensity={1.3} />
              <directionalLight position={[-3, 3, -3]} intensity={0.35} color="#ff6b7a" />
              <TerrainMesh
                sourceImageData={sourceImageData}
                colorImageData={colorImageData}
                heightScale={heightScale}
                autoRotate={autoRotate}
              />
              <gridHelper args={[3, 12, "#2a2b31", "#191a20"]} position={[0, -0.02, 0]} />
              <OrbitControls
                enableDamping
                dampingFactor={0.1}
                target={[0, 0, 0]}
              />
            </Canvas>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-zinc-500">
              Upload a height map to see it in 3D.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TerrainMesh = ({ sourceImageData, colorImageData, heightScale, autoRotate }) => {
  const meshRef = useRef();

  // Build geometry once (heights baked into a Float32 texture-like array).
  const { geometry, texture, heights, gw, gh } = useMemo(() => {
    const src = sourceImageData;
    const target = 128;
    const w = src.width;
    const h = src.height;
    const scale = Math.max(1, Math.floor(Math.max(w, h) / target));
    const gw = Math.max(4, Math.floor(w / scale));
    const gh = Math.max(4, Math.floor(h / scale));

    const heights = new Float32Array(gw * gh);
    const sd = src.data;
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        const sx = Math.min(w - 1, x * scale);
        const sy = Math.min(h - 1, y * scale);
        const i = (sy * w + sx) * 4;
        const lum = 0.2126 * sd[i] + 0.7152 * sd[i + 1] + 0.0722 * sd[i + 2];
        heights[y * gw + x] = lum / 255;
      }
    }

    const aspect = w / h;
    const px = aspect >= 1 ? 2 : 2 * aspect;
    const pz = aspect >= 1 ? 2 / aspect : 2;

    const geo = new THREE.PlaneGeometry(px, pz, gw - 1, gh - 1);
    geo.rotateX(-Math.PI / 2);

    const canvas = document.createElement("canvas");
    canvas.width = colorImageData.width;
    canvas.height = colorImageData.height;
    canvas.getContext("2d").putImageData(colorImageData, 0, 0);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.needsUpdate = true;

    return { geometry: geo, texture: tex, heights, gw, gh };
  }, [sourceImageData, colorImageData]);

  // Update vertex Y whenever heightScale changes — no need to rebuild geometry.
  useEffect(() => {
    const pos = geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, (heights[i] ?? 0) * heightScale);
    }
    pos.needsUpdate = true;
    geometry.computeVertexNormals();
  }, [geometry, heights, heightScale]);

  useEffect(() => {
    return () => {
      geometry.dispose();
      texture.dispose();
    };
  }, [geometry, texture]);

  useFrame((state, delta) => {
    if (autoRotate && meshRef.current) {
      meshRef.current.rotation.y += delta * 0.06;
    }
  });

  return (
    <mesh ref={meshRef} geometry={geometry} castShadow receiveShadow>
      <meshStandardMaterial
        map={texture}
        roughness={0.75}
        metalness={0.05}
        transparent
      />
    </mesh>
  );
};
